<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useListStoreNew } from "@/stores/ListStoreNew";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import { useRoute, useRouter, onBeforeRouteUpdate } from "vue-router";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import Reference from "@/components/ReferenceNumber.vue";
import dayjs from "dayjs";
import apiClient from "@/apiClient.js";
import SearchCustomer from "@/components/SearchCustomer.vue";
import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  PencilIcon,
  PlusIcon,
} from "@heroicons/vue/24/solid";

var relativeTime = require("dayjs/plugin/relativeTime");
var localisedFormat = require("dayjs/plugin/localizedFormat");
dayjs.extend(relativeTime);
dayjs.extend(localisedFormat);

const list = useListStoreNew();
const route = useRoute();
const router = useRouter();
const filter = ref("outstanding");
const searchQuery = ref("");
const exactAmount = ref("");
const amountVat = ref("inclusive");
const searchError = ref("");
const sortBy = ref("next_action");
const sortDirection = ref("ASC");
const sortOptions = [
  { value: "next_action", label: "Next action" },
  { value: "due_date", label: "Due date" },
  { value: "age", label: "Age" },
  { value: "updated_at", label: "Updated" },
  { value: "issue_date", label: "Issue date" },
  { value: "paid_at", label: "Paid date" },
  { value: "total", label: "Total" },
  { value: "reference", label: "Reference" },
];
const filterViews = [
  { value: "unsent", label: "Unsent" },
  { value: "scheduled", label: "Scheduled" },
  { value: "outstanding", label: "Outstanding" },
  { value: "overdue", label: "Overdue" },
  { value: "unpaid", label: "Unpaid" },
  { value: "paid", label: "Paid" },
  { value: "all", label: "All" },
];
const doubtfulStatuses = ["doubtful", "unrecoverable", "written_off"];
const createOpen = ref(false);
const creating = ref(false);
const referenceTypes = ref([]);
const selectedCustomer = ref({});
const invoiceType = ref("");
const invoiceDescription = ref("");
const actionsOpen = ref(false);
const actionsMenuRef = ref(null);
const purchaseOrderOpen = ref(false);
const purchaseOrderSaving = ref(false);
const purchaseOrderError = ref("");
const purchaseOrderCustomer = ref({});
const purchaseOrderNumber = ref("");
const purchaseOrderDescription = ref("");
const purchaseOrderLimit = ref("");
const purchaseOrderCandidateInvoices = ref([]);
const selectedPurchaseOrderInvoiceIds = ref([]);
const setUrl = (page = 1) => router.push({
  path: route.path,
  query: {
    af: filter.value,
    sort: sortBy.value,
    direction: sortDirection.value,
    query: searchQuery.value || undefined,
    amount: exactAmount.value || undefined,
    amount_vat: exactAmount.value ? amountVat.value : undefined,
    page,
  },
});
const changePage = (delta) => setUrl(list.getPage + delta);
const { setBreadcrumb } = useBreadcrumbs();

const rows = computed(() => list.getIndex);
const currentView = computed(
  () => filterViews.find((view) => view.value === filter.value) ?? filterViews[0],
);
const totalResults = computed(() => list.index?.value?.total ?? rows.value.length);
const showingFrom = computed(() => list.index?.value?.from ?? (rows.value.length ? 1 : 0));
const showingTo = computed(() => list.index?.value?.to ?? rows.value.length);
const normaliseFilter = (value) => {
  if (value === "all_unpaid") return "unpaid";
  if (["active", "needs_chasing", "plans", "long_term", "doubtful"].includes(value)) return "outstanding";
  return filterViews.some((option) => option.value === value) ? value : "outstanding";
};

onMounted(async () => {
  list.configure({ remote: "/api/invoice", local: "/invoice" });
  list.listService.setPage(route.query.page ?? 1);
  filter.value = normaliseFilter(route.query?.af);
  searchQuery.value = route.query.query ?? "";
  exactAmount.value = route.query.amount ?? "";
  amountVat.value = route.query.amount_vat === "exclusive" ? "exclusive" : "inclusive";
  sortBy.value = route.query?.sort ?? "next_action";
  sortDirection.value = route.query?.direction ?? "ASC";
  loadReferenceTypes();
  applyFilter();
  applySort();
  await list.fetchIndex();
  document.addEventListener("click", handleDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick);
});

onBeforeRouteUpdate((to) => {
  filter.value = normaliseFilter(to.query.af);
  searchQuery.value = to.query.query ?? "";
  exactAmount.value = to.query.amount ?? "";
  amountVat.value = to.query.amount_vat === "exclusive" ? "exclusive" : "inclusive";
  sortBy.value = to.query.sort ?? "next_action";
  sortDirection.value = to.query.direction ?? "ASC";
  applyFilter();
  applySort();
  list.listService.setPage(to.query.page ?? 1);
  list.fetchIndex();
});

const applyFilter = () => {
  list.clearFilters();
  list.addFilter("collection_view", filter.value);
  if (searchQuery.value.trim()) list.addFilter("query", encodeURIComponent(searchQuery.value.trim()));
  if (exactAmount.value) {
    list.addFilter("amount", exactAmount.value);
    list.addFilter("amount_vat", amountVat.value);
  }
};

const applySort = () => {
  if (!list.listService) {
    return;
  }
  list.listService.column = sortBy.value;
  list.listService.direction = sortDirection.value;
};

const setFilter = (val) => {
  filter.value = val;
  load();
};

const load = async () => {
  if (!list.listService) {
    return;
  }
  searchError.value = "";
  if (exactAmount.value && !/^\d{1,10}(?:\.\d{1,2})?$/.test(exactAmount.value)) {
    searchError.value = "Enter an exact amount in pounds, with up to two decimal places.";
    return;
  }
  applyFilter();
  applySort();
  list.resetPageWithoutFetching();
  const navigationFailure = await setUrl();
  if (navigationFailure) await list.fetchIndex();
};

const setSort = (column) => {
  if (sortBy.value === column) {
    sortDirection.value = sortDirection.value === "ASC" ? "DESC" : "ASC";
  } else {
    sortBy.value = column;
    sortDirection.value = column === "next_action" ? "ASC" : "DESC";
  }
  load();
};

const sortIndicator = (column) => {
  if (sortBy.value !== column) {
    return "";
  }
  return sortDirection.value === "ASC" ? "▲" : "▼";
};

const sortClass = (column) =>
  sortBy.value === column
    ? "text-slate-900 dark:text-slate-100"
    : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300";

const view = (val, clickEvent = null) => {
  if (
    clickEvent?.shiftKey ||
    clickEvent?.ctrlKey ||
    clickEvent?.altKey ||
    clickEvent?.metaKey
  ) {
    return;
  }

  router.push({
    name: "invoice-view",
    params: { id: val.id },
  });
  setBreadcrumb(val?.reference?.name ?? "");
};

const openNewTab = (event, row) => {
  if (event.button === 1) {
    open("/invoice/" + row.id, "_blank");
  }
};

const loadReferenceTypes = async () => {
  try {
    const { data } = await apiClient.get("/api/reference/types");
    referenceTypes.value = data ?? [];
  } catch (error) {
    console.error(error);
  }
};


const handleDocumentClick = (event) => {
  if (actionsMenuRef.value?.contains(event.target)) {
    return;
  }
  actionsOpen.value = false;
};

const toggleActions = () => {
  actionsOpen.value = !actionsOpen.value;
};

const openCreateModal = () => {
  actionsOpen.value = false;
  selectedCustomer.value = {};
  invoiceType.value = "";
  invoiceDescription.value = "";
  createOpen.value = true;
};

const closeCreateModal = () => {
  createOpen.value = false;
};

const customerSelected = (customer) => {
  selectedCustomer.value = customer;
};

const resetCustomer = () => {
  selectedCustomer.value = {};
};

const openPurchaseOrderModal = () => {
  actionsOpen.value = false;
  purchaseOrderCustomer.value = {};
  purchaseOrderNumber.value = "";
  purchaseOrderDescription.value = "";
  purchaseOrderLimit.value = "";
  purchaseOrderCandidateInvoices.value = [];
  selectedPurchaseOrderInvoiceIds.value = [];
  purchaseOrderError.value = "";
  purchaseOrderOpen.value = true;
};

const closePurchaseOrderModal = () => {
  if (purchaseOrderSaving.value) {
    return;
  }
  purchaseOrderOpen.value = false;
};

const purchaseOrderCustomerSelected = async (customer) => {
  purchaseOrderCustomer.value = customer;
  selectedPurchaseOrderInvoiceIds.value = [];
  purchaseOrderCandidateInvoices.value = [];
  purchaseOrderError.value = "";

  try {
    const { data } = await apiClient.get(`/api/customer/${customer.id}`);
    purchaseOrderCandidateInvoices.value = (data?.invoices ?? [])
      .filter((invoice) => !invoice?.customer_purchase_order_id)
      .sort((a, b) => Number(b.id ?? 0) - Number(a.id ?? 0));
  } catch (error) {
    purchaseOrderError.value = error?.response?.data?.message ?? "Unable to load customer invoices.";
  }
};

const resetPurchaseOrderCustomer = () => {
  purchaseOrderCustomer.value = {};
  purchaseOrderCandidateInvoices.value = [];
  selectedPurchaseOrderInvoiceIds.value = [];
};

const purchaseOrderInvoiceLabel = (invoice) => {
  const reference = invoice?.reference?.txt ?? `Invoice #${invoice?.id}`;
  const name = invoice?.reference?.name ?? "Invoice";
  return `${reference} · ${name}`;
};

const createPurchaseOrder = async () => {
  if (!purchaseOrderCustomer.value?.id) {
    purchaseOrderError.value = "Please select a customer.";
    return;
  }
  if (!purchaseOrderNumber.value.trim()) {
    purchaseOrderError.value = "Please enter a PO number.";
    return;
  }

  purchaseOrderSaving.value = true;
  purchaseOrderError.value = "";

  try {
    const poLimit = purchaseOrderLimit.value === ""
      ? null
      : Math.round(Number(purchaseOrderLimit.value) * 100);
    const { data } = await apiClient.post(`/api/customer/${purchaseOrderCustomer.value.id}/purchase-orders`, {
      po_number: purchaseOrderNumber.value.trim(),
      description: purchaseOrderDescription.value.trim() || null,
      po_total_amount: Number.isFinite(poLimit) ? poLimit : null,
    });

    const invoiceIds = selectedPurchaseOrderInvoiceIds.value
      .map((id) => Number(id))
      .filter(Boolean);

    if (invoiceIds.length) {
      await apiClient.post(`/api/purchase-orders/${data.id}/invoices`, {
        invoice_ids: invoiceIds,
      });
    }

    purchaseOrderOpen.value = false;
    load();
  } catch (error) {
    purchaseOrderError.value = error?.response?.data?.message ?? "Unable to create purchase order.";
  } finally {
    purchaseOrderSaving.value = false;
  }
};

const createInvoice = async () => {
  if (!selectedCustomer.value?.id) {
    alert("Please select a customer.");
    return;
  }
  if (!invoiceType.value) {
    alert("Please select a type.");
    return;
  }
  if (!invoiceDescription.value?.trim()) {
    alert("Please enter a description.");
    return;
  }

  creating.value = true;
  try {
    const referenceResponse = await apiClient.post("/api/reference", {
      customer_id: selectedCustomer.value.id,
      type_id: invoiceType.value,
      name: invoiceDescription.value,
    });
    const invoiceResponse = await apiClient.post("/api/invoice", {
      reference_id: referenceResponse.data.id,
    });
    closeCreateModal();
    router.push("/invoice/" + invoiceResponse.data.id);
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to create invoice.");
  } finally {
    creating.value = false;
  }
};

const invoiceTotal = (row) => (row?.subtotal ?? 0) + (row?.vat ?? 0);

const formatMoney = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format((value ?? 0) / 100);

const formatDate = (value) => {
  if (!value) {
    return "No date";
  }
  return dayjs(value).isValid() ? dayjs(value).format("D MMM YYYY") : "No date";
};

const dueDate = (row) =>
  row?.expected_at ?? row?.sent_at ?? row?.issue_date ?? row?.created_at ?? null;

const overdueDays = (row) => {
  if (row?.paid_at || !row?.sent_at) {
    return 0;
  }
  const date = dueDate(row);
  if (!date) {
    return 0;
  }
  return Math.max(0, dayjs().startOf("day").diff(dayjs(date).startOf("day"), "day"));
};

const isScheduled = (row) =>
  !row?.paid_at && row?.expected_at && dayjs(row.expected_at).isAfter(dayjs());

const paymentStatusLabel = (row) => {
  if (row?.paid_at) return "Paid";
  if (!row?.sent_at && isScheduled(row)) return "Scheduled";
  if (!row?.sent_at) return "Unsent";
  if (overdueDays(row) > 0) return "Overdue";
  return "Outstanding";
};

const paymentStatusClass = (row) => {
  if (row?.paid_at) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (!row?.sent_at && isScheduled(row)) return "bg-indigo-100 text-indigo-800 border-indigo-200";
  if (!row?.sent_at) return "bg-sky-100 text-sky-800 border-sky-200";
  if (isScheduled(row)) return "bg-teal-100 text-teal-800 border-teal-200";
  return "bg-rose-100 text-rose-800 border-rose-200";
};

const collectionStatus = (row) => row?.collection_status ?? "active";

const ageLabel = (row) => {
  if (row?.paid_at) return `Paid ${formatDate(row.paid_at)}`;
  if (!row?.sent_at) return paymentStatusLabel(row);
  const days = overdueDays(row);
  if (days === 0) return isScheduled(row) ? "Not due" : "Due today";
  if (days < 30) return `${days}d overdue`;
  if (days < 60) return "30-59d overdue";
  if (days < 90) return "60-89d overdue";
  if (days < 120) return "90-119d overdue";
  return "120d+ overdue";
};

const ageClass = (row) => {
  const days = overdueDays(row);
  if (row?.paid_at) return "text-emerald-700 dark:text-emerald-300";
  if (!row?.sent_at || days === 0) return "text-slate-500 dark:text-slate-400";
  if (days < 60) return "text-amber-700 dark:text-amber-300";
  if (days < 120) return "text-orange-700 dark:text-orange-300";
  return "text-rose-700 dark:text-rose-300";
};

const isActionDue = (row) => {
  if (row?.paid_at || doubtfulStatuses.includes(collectionStatus(row))) {
    return false;
  }
  if (!row?.collection_next_action_at) {
    return Boolean(row?.sent_at && !isScheduled(row));
  }
  return !dayjs(row.collection_next_action_at).isAfter(dayjs(), "day");
};

const nextActionLabel = (row) => {
  if (row?.paid_at) return "Closed";
  if (!row?.collection_next_action_at) {
    return row?.sent_at ? "No action set" : "Not sent";
  }
  return formatDate(row.collection_next_action_at);
};

const nextActionClass = (row) => {
  if (row?.paid_at) return "text-emerald-700 dark:text-emerald-300";
  if (!row?.collection_next_action_at) {
    return row?.sent_at
      ? "text-rose-700 dark:text-rose-300"
      : "text-slate-500 dark:text-slate-400";
  }
  if (isActionDue(row)) return "text-rose-700 dark:text-rose-300";
  return "text-teal-700 dark:text-teal-300";
};
</script>

<template>
  <div>
    <div class="box space-y-4">
      <div class="flex flex-col gap-3 xl:flex-row xl:items-center">
        <form class="flex min-w-0 flex-1 flex-wrap items-end gap-2" @submit.prevent="load">
          <label class="min-w-48 flex-1 text-xs font-medium text-slate-500">
            Search invoices
            <input v-model="searchQuery" class="input mt-1 h-10 w-full" type="search" placeholder="Reference, customer, description or PO" />
          </label>
          <label class="text-xs font-medium text-slate-500">
            Exact amount (£)
            <input v-model.trim="exactAmount" class="input mt-1 h-10 w-36" inputmode="decimal" placeholder="e.g. 120.00" />
          </label>
          <label class="text-xs font-medium text-slate-500">
            VAT
            <select v-model="amountVat" class="input mt-1 h-10">
              <option value="inclusive">Including VAT</option>
              <option value="exclusive">Excluding VAT</option>
            </select>
          </label>
          <button type="submit" class="btn h-10 rounded-full px-4">Search</button>
          <button type="button" class="btn h-10 rounded-full px-4" @click="searchQuery = ''; exactAmount = ''; load()">Clear</button>
          <p v-if="searchError" role="alert" class="w-full text-sm text-rose-600">{{ searchError }}</p>
        </form>

        <div class="flex flex-wrap items-center gap-2">
          <div class="flex min-w-0 flex-1 items-center gap-2 sm:flex-none">
            <span class="hidden text-xs font-semibold uppercase tracking-wide text-slate-500 md:inline">Sort</span>
            <select v-model="sortBy" @change="load" class="input h-10 min-w-0 flex-1 rounded-lg text-sm sm:w-40 sm:flex-none">
              <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
            <button class="btn flex h-10 items-center gap-2 rounded-lg px-3 text-sm" @click.prevent="setSort(sortBy)">
              <ChevronUpDownIcon class="h-4 w-4" />
              {{ sortDirection === "ASC" ? "Asc" : "Desc" }}
            </button>
          </div>
          <div ref="actionsMenuRef" class="relative">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-700 shadow-sm hover:bg-cyan-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-200 dark:hover:bg-cyan-900/50"
              :aria-expanded="actionsOpen"
              aria-haspopup="menu"
              aria-label="Open invoice list actions menu"
              @click.prevent.stop="toggleActions"
            >
              <span>Actions</span>
              <ChevronDownIcon class="h-4 w-4" />
            </button>
            <div
              v-if="actionsOpen"
              class="absolute right-0 top-full z-40 mt-2 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
              role="menu"
            >
              <div class="px-4 py-3">
                <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">Invoice Actions</p>
                <p class="text-xs text-slate-500 dark:text-slate-400">Create invoices and record customer POs.</p>
              </div>
              <div class="h-px bg-slate-200 dark:bg-slate-700"></div>
              <div class="py-2">
                <button class="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  type="button" role="menuitem" @click.prevent="openCreateModal">
                  <PlusIcon class="h-4 w-4" />
                  Create invoice
                </button>
                <button class="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  type="button" role="menuitem" @click.prevent="openPurchaseOrderModal">
                  <PencilIcon class="h-4 w-4" />
                  Record customer PO
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" role="group" aria-label="Invoice status filters">
        <button v-for="viewOption in filterViews" :key="viewOption.value" @click.prevent="setFilter(viewOption.value)"
          class="shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition"
          :aria-pressed="filter === viewOption.value"
          :class="filter === viewOption.value
            ? 'border-slate-900 bg-slate-900 text-white shadow-sm dark:border-slate-200 dark:bg-slate-100 dark:text-slate-900'
            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-500'">
          {{ viewOption.label }}
        </button>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-500">
        <span>Showing {{ showingFrom }}–{{ showingTo }} of {{ totalResults }} invoices</span>
        <span>{{ currentView.label }} value: <strong class="text-slate-900 dark:text-slate-100">{{ formatMoney(list.index?.value?.filtered_total ?? 0) }}</strong></span>
      </div>
    </div>

    <div v-if="!list.isLoading">
      <div class="box list hidden overflow-hidden p-0 lg:block">
        <table class="w-full">
          <thead>
            <tr>
              <th class="w-32 p-3 text-center">Reference</th>
              <th class="p-3">Customer</th>
              <th class="p-3">
                <button class="inline-flex items-center gap-1" :class="sortClass('reference')"
                  @click.prevent="setSort('reference')">
                  Project
                  <span class="text-[10px]">{{ sortIndicator("reference") }}</span>
                </button>
              </th>
              <th class="w-28 p-3 text-right">
                <button class="inline-flex items-center gap-1" :class="sortClass('total')"
                  @click.prevent="setSort('total')">
                  Total
                  <span class="text-[10px]">{{ sortIndicator("total") }}</span>
                </button>
              </th>
              <th class="w-32 p-3">
                <button class="inline-flex items-center gap-1" :class="sortClass('due_date')"
                  @click.prevent="setSort('due_date')">
                  Age
                  <span class="text-[10px]">{{ sortIndicator("due_date") }}</span>
                </button>
              </th>
              <th class="w-36 p-3">
                <button class="inline-flex items-center gap-1" :class="sortClass('next_action')"
                  @click.prevent="setSort('next_action')">
                  Next action
                  <span class="text-[10px]">{{ sortIndicator("next_action") }}</span>
                </button>
              </th>
              <th class="w-36 p-3">Status</th>
              <th class="w-32 p-3">
                <button class="inline-flex items-center gap-1" :class="sortClass('updated_at')"
                  @click.prevent="setSort('updated_at')">
                  Updated
                  <span class="text-[10px]">{{ sortIndicator("updated_at") }}</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id" class="cursor-pointer" @click="event => view(row, event)"
              @auxclick="event => openNewTab(event, row)">
              <td class="p-3 text-center">
                <Reference :reference="row?.reference" v-if="row?.reference?.type" class="w-24" />
              </td>
              <td class="max-w-44 truncate p-3">
                {{ row?.reference?.customer?.company }}
              </td>
              <td class="max-w-80 p-3">
                <div class="truncate font-medium text-slate-900 dark:text-slate-100">
                  {{ row?.reference?.name }}
                </div>
                <div class="mt-1 truncate text-xs text-slate-500">
                  PO {{ row.customer_purchase_order?.po_number || row.effective_purchase_order || row.purchase_order || "not set" }}
                </div>
              </td>
              <td class="p-3 text-right font-semibold text-slate-900 dark:text-slate-100">
                {{ formatMoney(invoiceTotal(row)) }}
              </td>
              <td class="p-3">
                <div class="font-medium" :class="ageClass(row)">{{ ageLabel(row) }}</div>
                <div class="mt-1 text-xs text-slate-500">{{ formatDate(dueDate(row)) }}</div>
              </td>
              <td class="p-3">
                <div class="font-medium" :class="nextActionClass(row)">
                  {{ nextActionLabel(row) }}
                </div>
                <div class="mt-1 text-xs text-slate-500" v-if="row.collection_next_action_at">
                  {{ dayjs(row.collection_next_action_at).fromNow() }}
                </div>
              </td>
              <td class="p-3">
                <div class="flex flex-wrap gap-1.5">
                  <span class="rounded-full border px-2 py-1 text-xs font-semibold" :class="paymentStatusClass(row)">
                    {{ paymentStatusLabel(row) }}
                  </span>
                </div>
              </td>
              <td class="p-3 text-sm text-slate-500">
                <span :title="formatDate(row?.reference?.updated_at)">
                  {{ dayjs(row?.reference?.updated_at).fromNow() }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="8" class="p-4 text-center text-slate-500" v-if="rows.length < 1">
                No invoices in this view
              </td>
            </tr>
            <tr>
              <th colspan="8" class="p-4">
                <PrevNextButtons :showPrev="list.showPrev" :showNext="list.showNext" @prev="changePage(-1)"
                  @next="changePage(1)" />
              </th>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="space-y-3 lg:hidden">
        <button v-for="row in rows" :key="row.id"
          class="w-full rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm dark:border-slate-700 dark:bg-slate-800"
          @click="event => view(row, event)" @auxclick="event => openNewTab(event, row)">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <Reference :reference="row?.reference" v-if="row?.reference?.type" class="mb-2 w-24" />
              <div class="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
                {{ row?.reference?.name }}
              </div>
              <div class="mt-1 truncate text-sm text-slate-500">
                {{ row?.reference?.customer?.company }}
              </div>
            </div>
            <div class="shrink-0 text-right text-base font-semibold text-slate-900 dark:text-slate-100">
              {{ formatMoney(invoiceTotal(row)) }}
            </div>
          </div>

          <div class="mt-4 flex flex-wrap gap-2">
            <span class="rounded-full border px-2 py-1 text-xs font-semibold" :class="paymentStatusClass(row)">
              {{ paymentStatusLabel(row) }}
            </span>
            <span class="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold"
              :class="ageClass(row)">
              {{ ageLabel(row) }}
            </span>
          </div>

          <div class="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Due</div>
              <div class="mt-1 text-slate-900 dark:text-slate-100">{{ formatDate(dueDate(row)) }}</div>
            </div>
            <div>
              <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Next action</div>
              <div class="mt-1 font-medium" :class="nextActionClass(row)">{{ nextActionLabel(row) }}</div>
            </div>
          </div>
        </button>

        <div class="box p-4 text-center text-slate-500" v-if="rows.length < 1">
          No invoices in this view
        </div>

        <div class="box p-4">
          <PrevNextButtons :showPrev="list.showPrev" :showNext="list.showNext" @prev="changePage(-1)"
            @next="changePage(1)" />
        </div>
      </div>
    </div>

    <div class="box list" v-else>
      <div class="space-y-3 lg:hidden">
        <div v-for="n in 8" :key="n" class="animate-pulse rounded-lg border border-slate-200 p-4">
          <div class="h-4 w-24 rounded bg-gray-300"></div>
          <div class="mt-3 h-5 rounded bg-gray-300"></div>
          <div class="mt-2 h-4 w-2/3 rounded bg-gray-300"></div>
        </div>
      </div>
      <table class="hidden w-full lg:table">
        <tbody>
          <tr v-for="n in 12" :key="n" class="animate-pulse border-b border-gray-300">
            <td class="p-3">
              <div class="h-6 rounded bg-gray-300"></div>
            </td>
            <td class="p-3">
              <div class="h-6 rounded bg-gray-300"></div>
            </td>
            <td class="p-3">
              <div class="h-6 rounded bg-gray-300"></div>
            </td>
            <td class="p-3">
              <div class="h-6 rounded bg-gray-300"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="purchaseOrderOpen"
      class="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur">
      <div class="w-full max-w-4xl rounded-lg bg-white shadow-2xl dark:bg-slate-800">
        <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div>
            <h2 class="text-xl font-semibold">Record Customer PO</h2>
            <p class="pt-1 text-sm text-slate-500 dark:text-slate-300">
              Record a PO supplied by the customer and optionally link existing unassigned invoices.
            </p>
          </div>
          <button class="btn" @click.prevent="closePurchaseOrderModal">
            Close
          </button>
        </div>

        <div class="max-h-[80vh] overflow-y-auto px-6 py-6">
          <form class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="md:col-span-2">
              <label>Customer</label>
              <SearchCustomer @selected="purchaseOrderCustomerSelected" v-show="!purchaseOrderCustomer.id" />
              <div class="flex items-center justify-between gap-3" v-show="purchaseOrderCustomer.id">
                <input type="text" :value="purchaseOrderCustomer.company" :disabled="true" class="input flex-1" />
                <button class="btn" @click.prevent="resetPurchaseOrderCustomer">
                  <PencilIcon class="h-4 w-4" />
                </button>
              </div>
            </div>

            <div>
              <label>PO Number</label>
              <input type="text" v-model="purchaseOrderNumber" class="input w-full" />
            </div>

            <div>
              <label>PO Limit</label>
              <input type="number" min="0" step="0.01" v-model="purchaseOrderLimit" class="input w-full"
                placeholder="Optional" />
            </div>

            <div class="md:col-span-2">
              <label>Description</label>
              <input type="text" v-model="purchaseOrderDescription" class="input w-full" placeholder="Optional" />
            </div>
          </form>

          <div class="mt-6">
            <div class="mb-2 flex items-center justify-between gap-3">
              <div>
                <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">Link invoices</h3>
                <p class="text-xs text-slate-500">Only invoices without a linked PO are shown.</p>
              </div>
              <div class="text-xs text-slate-500">
                {{ selectedPurchaseOrderInvoiceIds.length }} selected
              </div>
            </div>

            <div v-if="purchaseOrderCustomer.id && purchaseOrderCandidateInvoices.length"
              class="max-h-72 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <label v-for="invoice in purchaseOrderCandidateInvoices" :key="invoice.id"
                class="flex cursor-pointer items-start gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/40">
                <input v-model="selectedPurchaseOrderInvoiceIds" type="checkbox" class="mt-1" :value="invoice.id" />
                <div class="min-w-0 flex-1">
                  <div class="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {{ purchaseOrderInvoiceLabel(invoice) }}
                  </div>
                  <div class="truncate text-xs text-slate-500">
                    {{ invoice?.reference?.customer?.company || purchaseOrderCustomer.company }}
                  </div>
                </div>
                <div class="shrink-0 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {{ formatMoney(invoiceTotal(invoice)) }}
                </div>
              </label>
            </div>
            <div v-else-if="purchaseOrderCustomer.id"
              class="rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              No unlinked invoices found for this customer.
            </div>
            <div v-else
              class="rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Select a customer to choose invoices.
            </div>
          </div>

          <p v-if="purchaseOrderError" class="mt-4 text-sm text-red-600">{{ purchaseOrderError }}</p>

          <div class="mt-8 flex justify-end gap-2">
            <button class="btn" @click.prevent="closePurchaseOrderModal">
              Cancel
            </button>
            <button class="btn accent" @click.prevent="createPurchaseOrder" :disabled="purchaseOrderSaving">
              {{ purchaseOrderSaving ? "Recording..." : "Record Customer PO" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="createOpen" class="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur">
      <div class="w-full max-w-3xl rounded-lg bg-white shadow-2xl dark:bg-slate-800">
        <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div>
            <h2 class="text-xl font-semibold">Create Invoice</h2>
            <p class="pt-1 text-sm text-slate-500 dark:text-slate-300">
              Add the customer, type, and description to get started.
            </p>
          </div>
          <button class="btn" @click.prevent="closeCreateModal">
            Close
          </button>
        </div>

        <div class="max-h-[80vh] overflow-y-auto px-6 py-6">
          <form class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="md:col-span-2">
              <label>Customer</label>
              <SearchCustomer @selected="customerSelected" v-show="!selectedCustomer.id" />
              <div class="flex items-center justify-between gap-3" v-show="selectedCustomer.id">
                <input type="text" :value="selectedCustomer.company" :disabled="true" class="input flex-1" />
                <button class="btn" @click.prevent="resetCustomer">
                  <PencilIcon class="h-4 w-4" />
                </button>
              </div>
            </div>

            <div>
              <label>Type</label>
              <select v-model="invoiceType" class="input w-full">
                <option value="">- Select -</option>
                <option v-for="type in referenceTypes" :value="type?.id" :key="type?.id">
                  {{ type?.name }}
                </option>
              </select>
            </div>

            <div>
              <label>Description</label>
              <input type="text" v-model="invoiceDescription" class="input w-full"
                :class="{ 'border-red-500': invoiceDescription?.length < 1 }" />
            </div>
          </form>

          <div class="mt-8 flex justify-end gap-2">
            <button class="btn" @click.prevent="closeCreateModal">
              Cancel
            </button>
            <button class="btn accent" @click.prevent="createInvoice" :disabled="creating">
              {{ creating ? "Creating..." : "Create Invoice" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
